import { useState } from "react";
import { Button } from "../../../client/components/ui/button";
import { Input } from "../../../client/components/ui/input";
import {
  type AccountTypeForm,
  emptyTier,
  duplicateTier,
} from "./propFirmTierForm";
import { TierEditor } from "./TierEditor";
import { SELECT_CLASS } from "./formStyles";
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Layers,
  List,
} from "lucide-react";

// ─── AccountTypeEditor ────────────────────────────────────────────────────────

export function AccountTypeEditor({ at, index, onChange, onDelete, onRefresh, onBeforePhaseWrite, phasesByTierId }: {
  at: AccountTypeForm;
  index: number;
  onChange: (at: AccountTypeForm) => void;
  onDelete: () => void;
  onRefresh: () => void;
  /**
   * Sauvegarde le template avant qu'une phase ne soit ecrite. Renvoie `false`
   * si l'ecriture doit etre abandonnee. Simple relais vers `TierEditor`.
   */
  onBeforePhaseWrite: () => Promise<boolean>;
  /** Phases du template serveur, par id de palier. Simple relais vers `TierEditor`. */
  phasesByTierId: Map<string, any[]>;
}) {
  const [open, setOpen] = useState(true);
  const set = (field: keyof AccountTypeForm, value: any) => onChange({ ...at, [field]: value });

  const autoSlug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  return (
    <div className="border-2 border-primary/20 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-2 font-medium">
          <Layers className="w-4 h-4 text-primary" />
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          Type {index + 1}: {at.name || "(sans nom)"}
        </button>
        <Button type="button" variant="ghost" size="sm" onClick={onDelete} className="text-destructive">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {open && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-sm text-muted-foreground block mb-1">
                Nom<span className="text-destructive ml-0.5">*</span>
              </label>
              <Input
                value={at.name}
                onChange={e => {
                  set("name", e.target.value);
                  if (!at.slug || at.slug === autoSlug(at.name)) {
                    set("slug", autoSlug(e.target.value));
                  }
                }}
                placeholder="LucidPro, Standard, Aggressive..."
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground block mb-1">
                Slug (unique dans le template)<span className="text-destructive ml-0.5">*</span>
              </label>
              <Input
                value={at.slug}
                onChange={e => set("slug", e.target.value)}
                placeholder="lucidpro, standard..."
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground block mb-1">Sort Order</label>
              <Input type="number" value={at.sortOrder} onChange={e => set("sortOrder", parseInt(e.target.value) || 0)} />
            </div>
            <div className="flex items-end gap-2 pb-1">
              <input type="checkbox" id={`active-at-${index}`} checked={at.isActive} onChange={e => set("isActive", e.target.checked)} />
              <label htmlFor={`active-at-${index}`} className="text-sm">Actif</label>
            </div>
            <div className="col-span-2">
              <label className="text-sm text-muted-foreground block mb-1">Modèle de tarification</label>
              <select
                value={at.pricingModel}
                onChange={e => set("pricingModel", e.target.value)}
                className={SELECT_CLASS}
              >
                <option value="one_time">Paiement unique</option>
                <option value="subscription">Abonnement mensuel (pendant le challenge)</option>
                <option value="direct_funded">Direct funded (sans challenge)</option>
              </select>
            </div>
            <div className="col-span-2 border-t border-border pt-2">
              <p className="text-sm font-medium">Gel du plancher (trailing drawdown)</p>
              <p className="text-xs text-muted-foreground">
                Le plancher cesse de monter à « solde initial + décalage ». Vide = défaut : +100 $ chez Apex,
                0 ailleurs, gel quand le trailing atteint ce niveau, en funded seulement.
              </p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground block mb-1">Décalage ($)</label>
              <Input
                type="number"
                step="any"
                value={at.drawdownLockOffset}
                onChange={e => set("drawdownLockOffset", e.target.value)}
                placeholder="Défaut (100 Apex, 0 sinon)"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground block mb-1">Le plancher se fige</label>
              <select
                value={at.drawdownLockMode}
                onChange={e => set("drawdownLockMode", e.target.value)}
                className={SELECT_CLASS}
              >
                <option value="">Défaut (quand le trailing atteint le niveau)</option>
                <option value="trailing">Quand le trailing atteint le niveau</option>
                <option value="trailing_or_payout">Au trailing, ou dès le premier payout</option>
                <option value="never">Jamais (le trailing suit toujours le pic)</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-sm text-muted-foreground block mb-1">Phases concernées</label>
              <select
                value={at.drawdownLockScope}
                onChange={e => set("drawdownLockScope", e.target.value)}
                className={SELECT_CLASS}
              >
                <option value="">Défaut (funded seulement)</option>
                <option value="funded">Funded seulement</option>
                <option value="all">Challenge et funded</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium flex items-center gap-1"><List className="w-3 h-3" /> Tiers ({at.tiers.length})</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => set("tiers", [...at.tiers, emptyTier(at.tiers.length)])}
              >
                <Plus className="w-3 h-3 mr-1" /> Ajouter un tier
              </Button>
            </div>
            {at.tiers.map((tier, ti) => (
              <TierEditor
                /*
                  Cle STABLE, pas l'index : la duplication insere un palier au
                  milieu de la liste, ce qui decale toutes les positions
                  suivantes. Avec `key={ti}`, React rattacherait l'etat local
                  d'un palier (replie/deplie, et le formulaire de phase en
                  cours de saisie de `PhaseEditor`) a son VOISIN. Meme motif
                  que le crash de la modale d'ajout de compte. Repli sur
                  l'index si la cle manque : `clientKey` est optionnel dans le
                  type, la cle ne doit jamais valoir `undefined`.
                */
                key={tier.clientKey ?? ti}
                tier={tier}
                index={ti}
                pricingModel={at.pricingModel}
                onChange={t => set("tiers", at.tiers.map((x, i) => i === ti ? t : x))}
                /*
                  La copie est inseree JUSTE APRES sa source, pas ajoutee en
                  fin de liste : le product owner duplique pour faire varier
                  une ou deux valeurs, donc la copie doit apparaitre la ou il
                  regarde. `sortOrder` est renumerote sur TOUTE la liste, sinon
                  l'insertion au milieu laisserait deux paliers partager le
                  meme rang. `duplicateTier` suffixe le label en comparant aux
                  labels deja presents, ce qui evite le P2002 de la contrainte
                  @@unique([propFirmAccountTypeId, label]).
                */
                onDuplicate={() => {
                  const copy = duplicateTier(tier, at.tiers.map(t => t.label));
                  set(
                    "tiers",
                    [...at.tiers.slice(0, ti + 1), copy, ...at.tiers.slice(ti + 1)]
                      .map((t, i) => ({ ...t, sortOrder: i })),
                  );
                }}
                onDelete={() => set("tiers", at.tiers.filter((_, i) => i !== ti))}
                onRefresh={onRefresh}
                onBeforePhaseWrite={onBeforePhaseWrite}
                phasesByTierId={phasesByTierId}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
